import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecurityMonitoring } from '@/hooks/use-security-monitoring';
import { useAuth } from '@/context/AuthContext';

interface SiteSetting {
  key: string;
  value: string;
}

export function useSiteSettings() {
  const { user } = useAuth();
  const { logAdminAction } = useSecurityMonitoring(user);
  const [featuresTestEnabled, setFeaturesTestEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const fetchSettings = async () => {
    try {
      if (import.meta.env.DEV) console.log('Fetching site settings...');
      
      // Use rate-limited function instead of direct query
      const { data, error } = await supabase.rpc(
        'get_public_setting_with_rate_limit',
        { p_setting_key: 'features_test_enabled' }
      );

      if (error) {
        // Check for rate limit error
        if (error.message?.includes('Rate limit exceeded')) {
          console.warn('Rate limit exceeded for public settings');
          return;
        }
        
        if (import.meta.env.DEV) console.error('Error fetching settings:', error);
        return;
      }

      if (data && data.length > 0) {
        if (import.meta.env.DEV) console.log('Settings data received:', data);
        setFeaturesTestEnabled(data[0].value === 'true');
      } else {
        if (import.meta.env.DEV) console.log('No settings data found, using default');
      }
    } catch (error: any) {
      // Handle rate limit gracefully
      if (error?.message?.includes('Rate limit exceeded')) {
        console.warn('Public settings rate limit hit - using cached value');
      } else {
        if (import.meta.env.DEV) console.error('Error fetching site settings:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateFeaturesTestEnabled = async (enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('public_settings')
        .upsert(
          { key: 'features_test_enabled', value: enabled.toString() },
          { onConflict: 'key' }
        );

      if (error) {
        console.error('Error updating features test setting:', error);
        throw error;
      }

      // Log the feature toggle change for audit trail
      await logAdminAction(
        'feature_toggle_changed',
        'public_settings',
        'features_test_enabled',
        {
          setting_key: 'features_test_enabled',
          old_value: featuresTestEnabled,
          new_value: enabled,
          timestamp: new Date().toISOString()
        }
      );

      setFeaturesTestEnabled(enabled);
    } catch (error) {
      console.error('Error updating site settings:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    featuresTestEnabled,
    loading,
    updateFeaturesTestEnabled,
    refetch: fetchSettings,
  };
}